/**
 * stockLevelResource — the degraded bulk on-hand read surface (#1151),
 * authored against the resource-kit v2 model/view contracts and consumed
 * directly as a `ResourceBundle` by `ResourcePage` (#1805 slice A1). The four
 * cell projections moved to `./widgets.tsx` as
 * named widgets, because `fields[].render` has no legacy carrier; nothing an
 * operator sees moved with them.
 *
 * The list is the ingredient catalog (`ingredientAPI.list` →
 * `GET /api/catalog/ingredients`, server-paginated). The on-hand column is a
 * permanent em-dash: the rebuilt `/api` face has NO batch on-hand endpoint,
 * only per-id `GET /api/inventory/on-hand`, so a full-list on-hand fan-out is
 * withheld pending a batch endpoint (owner decision #935/#936). The
 * persistent "on-hand unavailable" notice is rendered by the thin
 * `StockLevelsPage` wrapper that composes this resource (the framework has no
 * page-body notice slot).
 *
 * Read-only: no create/edit/delete, no filter whitelist (the endpoint accepts
 * none), so every field opts out of filter generation (`filterable: false`) and
 * no QueryBuilder / quick filter renders — parity with the bespoke page, which
 * offered only the free-text search box and the table. Row click opens the
 * framework detail drawer (`detail.surface: 'drawer'`); there is no `/:id`
 * route for a stock-level detail.
 *
 * Route-gated by `<PermissionGuard>` in `App.tsx` (INVENTORY_READ AND
 * CATALOG_READ), so no `permission` block here.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { ingredientAPI, toListResult } from '@/services';
import type { Ingredient } from '@/services';
import { CATALOG_INGREDIENT_SORTS } from '@/services/filters.generated';

const f = createFieldConstructors<Ingredient>();

export const stockLevelModel = defineModel<Ingredient>()({
  name: 'stock-levels',
  idField: 'id',
  recName: 'name',
  fields: {
    name: f.text({
      label: 'inventory.stock.col.name',
      filterable: false,
      widget: 'stockIngredientName',
    }),
    unit: f.text({
      label: 'inventory.stock.col.unit',
      filterable: false,
      widget: 'stockUnit',
    }),
    category: f.text({
      label: 'inventory.stock.col.category',
      filterable: false,
      widget: 'stockCategory',
    }),
    // Display-only degraded column: no accessor value, a fixed em-dash
    // (#1405 `virtual` branch — no backing property on the row type).
    on_hand: f.virtual.text({
      label: 'inventory.stock.col.onHand',
      filterable: false,
      widget: 'stockOnHand',
    }),
  },
});

export const stockLevelListView = defineListView(stockLevelModel, {
  items: [{ field: 'name' }, { field: 'unit' }, { field: 'category' }, { field: 'on_hand' }],
  // The framework default is hidden since #1642. This list keeps its box, and
  // no longer on a comment's word: `GET /api/catalog/ingredients` declares
  // `x-searchable: ["name"]`, which reaches here as `CATALOG_INGREDIENT_SEARCH`
  // and is compared against this flag by `resource-searchable-parity.test.ts`
  // in both directions (#1669). The kitchen handler — not the list kernel — is
  // what applies the narrowing, so a backend integration case pins that too.
  capabilities: { search: true },
});

/** Read-only surface: the drawer shows the same columns; there is no form. */
export const stockLevelFormView = defineFormView(stockLevelModel, {
  groupDisplay: 'tab',
  children: [],
});

export const stockLevelResource: ResourceBundle<Ingredient, typeof stockLevelModel.fields> = {
  model: stockLevelModel,
  list: stockLevelListView,
  form: stockLevelFormView,
  host: {
    api: {
      // `ingredientAPI.list` returns the `{ items, total, page, size }` list
      // body (server-paginated), renamed here to the framework's `ListResult`
      // (#2167 PR-2). ListParams is shape-compatible with the service's
      // ListRequestParams.
      list: async (params) => toListResult(await ingredientAPI.list(params)),
    },
    label: 'inventory.stock.title',
    slug: 'stock-levels',
    listTitle: 'inventory.stock.title',
    // This list IS `GET /api/catalog/ingredients`, which declares
    // `x-sortable: []` — the generated constant is empty and is bound here by
    // reference, the same way the ingredient resource binds its filters. Until
    // #2114 an absent declaration read as "unrestricted" and `createColumns`
    // marked all four headers sortable, including the em-dash `on_hand`
    // column that has no backing value at all.
    sortable: CATALOG_INGREDIENT_SORTS,
    detail: { surface: 'drawer' },
  },
};
